import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import {
	CheckChangeEmailResponse,
	CreateSubscriber,
	CancellationOfferEligibility,
	ExtendedPlan,
	PaymentResponse,
	Subscriber,
	SubscriberPlan,
	SubscriberPlanPayment,
	SubscriberResponse,
	UpdateSubscriber,
	UpdateSubscriberPlan,
	ValidationCode
} from '@interfaces'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { ChargeResponse } from '../../interfaces/Charge.interface'

@Injectable({
	providedIn: 'root'
})
export class SubscriberService {
	private http = inject(HttpClient)
	private url = '/subscribers'

	private _refreshSubscriberDetails = new Subject<void>()
	public refreshSubscriberDetails$ = this._refreshSubscriberDetails.asObservable()

	private _subscriberDetails = new BehaviorSubject<Subscriber | null>(null)
	public subscriberDetails$ = this._subscriberDetails.asObservable()

	private _isEditingPlan = new BehaviorSubject<boolean>(false)
	public isEditingPlan$ = this._isEditingPlan.asObservable()

	getSubscribers(page: number = 0, size: number = 15, active: boolean = true) {
		const url = `${this.url}?page=${page}&size=${size}&active=${active}`

		return this.http.get<SubscriberResponse>(url)
	}
	getSubscribersBar(page: number = 1, size: number = 15, active: boolean = true) {
		return this.http.get<SubscriberResponse>(`${this.url}/bar`, {
			params: { page, size, active }
		})
	}

	getSubscriberDetails(subscriberId: number): Observable<Subscriber> {
		const url = `${this.url}/${subscriberId}`
		return this.http.get<Subscriber>(url)
	}

	deleteSubscriber(subscriberId: number): Observable<Subscriber> {
		const url = `${this.url}/${subscriberId}`
		return this.http.delete<Subscriber>(url)
	}

	getSubscriberCharges(subscriberId: number, page: number = 1, size: number = 15): Observable<ChargeResponse> {
		const url = `${this.url}/${subscriberId}/payments?page=${page}&size=${size}`
		return this.http.get<ChargeResponse>(url)
	}

	createSubscriber(subscriber: CreateSubscriber): Observable<Subscriber> {
		return this.http.post<Subscriber>(this.url, subscriber)
	}

	updateSubscriber(subscriber: UpdateSubscriber): Observable<Subscriber> {
		return this.http.put<Subscriber>(this.url, subscriber)
	}

	checkChangeEmail(oldEmail: string, newEmail: string): Observable<CheckChangeEmailResponse> {
		const body = {
			oldEmail: oldEmail,
			newEmail: newEmail
		}
		const url = `${this.url}/checkChangeEmail`
		return this.http.post<CheckChangeEmailResponse>(url, body)
	}

	updateSubscriberPlan(subscriber: UpdateSubscriberPlan): Observable<SubscriberPlan> {
		const url = `${this.url}/${subscriber.id}/subscriberPlan`
		return this.http.put<SubscriberPlan>(url, subscriber)
	}

	updateSubscriberPaymentMethod(
		subscriberId: number,
		paymentToken: string,
		externalPlatform: boolean
	): Observable<SubscriberPlan> {
		const body = {
			paymentToken: paymentToken,
			externalPlatform: externalPlatform
		}
		const url = `${this.url}/${subscriberId}/subscriberPayMethod`
		return this.http.put<SubscriberPlan>(url, body)
	}

	updateSubscriberPassword(subscriberId: number, newPassword: string, repeatPassword: string): Observable<string> {
		const url = `${this.url}/${subscriberId}/password`
		return this.http.put<string>(url, { newPassword, repeatPassword })
	}

	createSubscriberPlan(
		subscriberId: number,
		subscriberPlanPayment: SubscriberPlanPayment
	): Observable<PaymentResponse> {
		const url = `${this.url}/${subscriberId}/subscriberPlan`
		return this.http.post<PaymentResponse>(url, subscriberPlanPayment)
	}

	cancelPlanChange(subscriberId: number) {
		const url = `${this.url}/${subscriberId}/cancelPlanChange`
		return this.http.get<SubscriberPlan>(url)
	}

	cancelExtensionPlan(extensionPlan: ExtendedPlan, subscriberId: number) {
		const url = `${this.url}/${subscriberId}/cancelExtensionPlan`
		return this.http.put<SubscriberPlan>(url, extensionPlan)
	}

	setSubscriberStatus(ids: number[], active: boolean = true) {
		const url = `${this.url}/status?active=${active}`
		return this.http.put<boolean>(url, ids)
	}

	disableSubscriberPlan(ids: number[], forceCancellation: boolean = false) {
		const url = `${this.url}/status`
		return this.http.put<boolean>(url, { ids, forceCancellation })
	}

	cancelUnsubscription(ids: number[]) {
		const url = `${this.url}/cancel-unsubscription`
		return this.http.put<boolean>(url, ids)
	}

	getEligibleCancellationOffer(subscriberId: number): Observable<CancellationOfferEligibility> {
		const url = `${this.url}/${subscriberId}/cancellation-offer`
		return this.http.get<CancellationOfferEligibility>(url)
	}

	instantUnsubscribe(ids: number[], forceCancellation: boolean = false) {
		const url = `${this.url}/unsubscribe`
		return this.http.put<boolean>(url, { ids, forceCancellation })
	}

	setSpamStatus(ids: number[]) {
		const url = `${this.url}/spam`
		return this.http.put<boolean>(url, ids)
	}

	setSubscriberDetails(subscriber: Subscriber | null): void {
		this._subscriberDetails.next(subscriber)
	}

	validateOffers(code: { planId: number; code: string; subscriberId: number }): Observable<ValidationCode> {
		const url = `/offers/validate-code`

		return this.http.post<ValidationCode>(url, code)
	}

	extendPlan(extendPlan: ExtendedPlan, subscriberId?: number) {
		const url = `${this.url}/${subscriberId}/extendPlan`

		return this.http.post<ValidationCode>(url, extendPlan)
	}

	refreshSubscriberDetails(): void {
		this.setIsEditingPlan(false)
		this._refreshSubscriberDetails.next()
	}

	setIsEditingPlan(editing: boolean = false): void {
		this._isEditingPlan.next(editing)
	}
}
